// The web Apopilo offer composer (Figma eWr6sqSGfDufrRNUMjbtK4 `Web / Apopilo / Composer modal` 2773:1282; Edit offer
// 3004:10220 with its confirm 3005:7488): create an APOPILO post (a Tutor or an Apopilo offer), or edit one from its ⋯
// menu. Like the event and book composers, Post is never disabled: a tap with missing or invalid fields shows the red
// reason under each, and a change to a field clears its error. An edit also offers "Cancel service" while the offer is
// taken and has not started.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { AlignLeft, Building2, CalendarDays, Map as MapIcon, Type, X } from "lucide-react";
import { ContentError, createPostV2, getPostV2, updatePostV2, type ApopiloKind, type PostV2 } from "../../api/content";
import { cancelStay } from "../../api/apopilo";
import { IMAGE_ACCEPT, SvgTooLargeError, uploadImage } from "../../api/upload";
import { invalidateApopilo } from "../../hooks/useApopilo";
import { DEFAULT_MAP_CENTER } from "../../lib/mapDefaults";
import { useMapStore } from "../../store/mapStore";
import { useComposerDraftStore } from "../../store/composerDraftStore";
import { contentErrorText } from "../wall/contentErrorText";
import LiveConfirmDialog from "../stream/LiveConfirmDialog";
import LocationPickerModal, { formatCoords } from "../events/LocationPickerModal";
import { formatPrice, formatUnits } from "../events/eventFormat";
import { newOfferDraft, offerDraftOf, offerErrors, offerTotalEuros, offerUnits, toOfferInput, todayKey, type OfferDraft, type OfferField } from "./offerDraft";

interface Props {
  /** The APOPILO post to edit; null = a new offer. */
  existing: PostV2 | null;
  onClose(): void;
  onSaved(post: PostV2): void;
}

const MAX_PHOTOS = 10;
// Field row (Figma "Field · …"): an icon, then the text; the row is the elevated well.
const ROW = "flex items-center gap-3 rounded-lg bg-[var(--bg-elevated)] p-3 text-[var(--foreground)] focus-within:ring-2 focus-within:ring-[var(--primary-text)]";
const INPUT = "min-w-0 flex-1 bg-transparent text-base text-[var(--foreground)] outline-none placeholder:text-[var(--foreground-subtle)] disabled:cursor-not-allowed disabled:opacity-60";
const SECTION = "text-sm font-medium text-[var(--foreground-muted)]";
const LINK = "shrink-0 text-sm font-bold text-[var(--primary)] dark:text-[var(--primary-on-surface)] cursor-pointer";
const ICON = "shrink-0 text-[var(--foreground-muted)]";

function Err({ id, children }: { id: string; children: ReactNode }) {
  return <p data-testid={`offer-composer-${id}-error`} role="alert" className="m-0 text-xs text-[var(--destructive-text)]">{children}</p>;
}

const tomorrowKey = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return todayKey(d);
};

export default function OfferComposerModal({ existing, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const initial = useRef<OfferDraft>(existing ? offerDraftOf(existing) : newOfferDraft());
  const [draft, setDraft] = useState<OfferDraft>(initial.current);
  const [shown, setShown] = useState<Partial<Record<OfferField, true>>>({});
  const [uploading, setUploading] = useState(0);
  /** Why a photo did not upload: "svg" = an SVG over file-stc's 1 MiB cap (refused before upload). */
  const [photoFailed, setPhotoFailed] = useState<false | "failed" | "svg">(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const photoInput = useRef<HTMLInputElement>(null);
  const discarded = useRef(false);

  const info = existing?.apopilo ?? null;
  const stay = info?.stay ?? null;
  // Dates are locked while requests wait, and while the offer is taken (it must be cancelled first).
  const datesLocked = info != null && (info.status === "TAKEN" || (info.status === "OPEN" && info.requestCount > 0));
  const canCancelStay = existing != null && stay != null && stay.canCancel && !info!.started;

  // Dirty = changed fields or an upload in flight; the X, a stray backdrop tap or Escape asks before throwing it away.
  const dirty = uploading > 0 || JSON.stringify(draft) !== JSON.stringify(initial.current);
  useEffect(() => {
    useComposerDraftStore.getState().set({ dirty });
  }, [dirty]);
  useEffect(() => () => useComposerDraftStore.getState().reset(), []);
  const pendingDiscard = useComposerDraftStore((s) => s.pending);
  const guardedClose = () => useComposerDraftStore.getState().guard(() => { discarded.current = true; onClose(); });
  const guardedCloseRef = useRef(guardedClose);
  guardedCloseRef.current = guardedClose;
  useEffect(() => {
    useComposerDraftStore.getState().set({ requestClose: () => guardedCloseRef.current() });
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !e.defaultPrevented) guardedCloseRef.current(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /** Edits a field; its shown error (if any) goes away at once. */
  function patch(p: Partial<OfferDraft>, field?: OfferField) {
    setDraft((d) => ({ ...d, ...p }));
    if (field) setShown((s) => { const { [field]: _gone, ...rest } = s; return rest; });
  }

  async function pickPhotos(files: FileList | null) {
    if (!files || files.length === 0) return;
    setPhotoFailed(false);
    const room = MAX_PHOTOS - draft.photos.length;
    for (const file of Array.from(files).slice(0, Math.max(room, 0))) {
      setUploading((n) => n + 1);
      try {
        const up = await uploadImage(file);
        setDraft((d) => ({ ...d, photos: [...d.photos, { fileId: up.id, url: up.url }] }));
      } catch (err) {
        setPhotoFailed(err instanceof SvgTooLargeError ? "svg" : "failed");
      } finally {
        setUploading((n) => n - 1);
      }
    }
  }

  /** A 409 APOPILO_* means the offer changed under the user: the lists refresh whatever the outcome. */
  const failed = (e: unknown) => {
    const code = e instanceof ContentError ? e.code : "UNKNOWN";
    if (e instanceof ContentError && e.status === 409 && code.startsWith("APOPILO_")) invalidateApopilo(qc);
    // The server's date rule (Europe/Madrid "today") can refuse dates the browser let through: say it under the dates.
    if (code === "APOPILO_DATES_INVALID") setShown((s) => ({ ...s, dates: true }));
    else setSubmitError(code);
  };

  async function submit() {
    if (submitting) return;
    setSubmitError(null);
    const errs = offerErrors(draft, todayKey());
    // Dates the server already holds are not asked again (an offer starting soon can still change its text).
    if (existing && draft.startsOn === initial.current.startsOn && draft.endsOn === initial.current.endsOn && draft.kind === initial.current.kind) delete errs.dates;
    if (Object.keys(errs).length > 0 || uploading > 0) {
      setShown(errs);
      if (uploading > 0) setSubmitError("OFFER_PHOTO_UPLOADING");
      return;
    }
    setSubmitting(true);
    try {
      const input = toOfferInput(draft);
      const saved = existing ? await updatePostV2(existing.id, input) : await createPostV2(input);
      invalidateApopilo(qc);
      discarded.current = true;
      onSaved(saved);
    } catch (e) {
      failed(e);
    } finally {
      setSubmitting(false);
    }
  }

  async function cancelService() {
    if (!stay || submitting) return;
    setConfirmCancel(false);
    setSubmitError(null);
    setSubmitting(true);
    try {
      await cancelStay(stay.id);
      invalidateApopilo(qc);
      discarded.current = true;
      // The open post is replaced by its fresh copy (now on hold), so a stale "Cancel service" is not offered again.
      const fresh = await getPostV2(existing!.id).catch(() => null);
      if (fresh) onSaved(fresh);
      else onClose();
    } catch (e) {
      failed(e);
    } finally {
      setSubmitting(false);
    }
  }

  const heading = existing ? t("apopilo_composer_edit", "Edit offer") : t("apopilo_composer_new", "New offer");
  const submitText =
    submitError === "OFFER_PHOTO_UPLOADING" ? t("apopilo_composer_photo_wait", "Wait for the photo to finish uploading")
      : submitError ? contentErrorText(t, submitError, t("wall_error_UNKNOWN", "Something went wrong — try again")) : null;
  const kinds: { id: ApopiloKind; label: string }[] = [
    { id: "TUTOR", label: t("apopilo_composer_kind_tutor", "Tutor · 20€ / day") },
    { id: "APOPILO", label: t("apopilo_composer_kind_apopilo", "Apopilo · paid in days") },
  ];
  const units = offerUnits(draft);
  const euros = offerTotalEuros(draft);
  const live = units != null && units >= 1
    ? `${formatUnits(draft.kind, units, t)}${euros != null ? ` · ${formatPrice(euros * 100, "EUR", { spaced: true })}` : ""}`
    : null;
  const range = `${initial.current.startsOn} – ${initial.current.endsOn}`;

  const discardDialog =
    pendingDiscard &&
    createPortal(
      <div style={{ display: "contents" }} onClick={(e) => e.stopPropagation()}>
        <LiveConfirmDialog
          testId="discard-draft-dialog"
          zClass="z-[2200]"
          message={t("wall_discard_draft_message", "Discard this post? Your changes will be lost.")}
          confirmLabel={t("wall_discard_draft_confirm", "Discard")}
          cancelLabel={t("wall_discard_draft_keep", "Keep editing")}
          confirmTestId="discard-draft-confirm"
          onConfirm={() => useComposerDraftStore.getState().confirmDiscard()}
          onCancel={() => useComposerDraftStore.getState().cancelDiscard()}
        />
      </div>,
      document.body,
    );

  // Figma 3005:7488: a centred title and text, then two stacked text buttons (destructive, then primary).
  const cancelDialog =
    confirmCancel &&
    createPortal(
      <div style={{ display: "contents" }} onClick={(e) => e.stopPropagation()}>
        <div data-testid="offer-composer-cancel-dialog" className="fixed inset-0 z-[2200] flex items-center justify-center bg-[color-mix(in_srgb,var(--foreground)_40%,transparent)]"
          onClick={() => setConfirmCancel(false)}>
          <div role="dialog" aria-modal="true" aria-label={t("apopilo_composer_cancel_title", "Cancel this service?")}
            className="flex w-[min(400px,calc(100vw-32px))] flex-col gap-3 rounded-2xl bg-[var(--bg-surface)] p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="m-0 text-base font-bold text-[var(--foreground)]">{t("apopilo_composer_cancel_title", "Cancel this service?")}</h2>
            <p className="m-0 text-sm text-[var(--foreground-muted)]">
              {t("apopilo_composer_cancel_body", "The stay ({{range}}) is cancelled and we tell the guest. Your offer goes on hold: post it again or delete it from your page.", { range })}
            </p>
            <button type="button" data-testid="offer-composer-cancel-confirm" className="h-11 text-sm font-bold text-[var(--destructive-text)]" onClick={() => void cancelService()}>
              {t("apopilo_composer_cancel_service", "Cancel service")}
            </button>
            <button type="button" data-testid="offer-composer-cancel-keep" autoFocus className="h-11 text-sm font-bold text-[var(--primary)] dark:text-[var(--primary-on-surface)]" onClick={() => setConfirmCancel(false)}>
              {t("apopilo_composer_keep", "Keep it")}
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <div role="dialog" aria-modal="true" aria-label={heading} data-book-modal data-testid="offer-composer"
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-[color-mix(in_srgb,var(--foreground)_40%,transparent)]"
      onClick={() => guardedCloseRef.current()}>
      <div className="flex max-h-[90vh] w-[560px] max-w-full flex-col overflow-y-auto rounded-2xl bg-[var(--bg-surface)] shadow-xl" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center gap-4 px-4 py-3">
          <button type="button" aria-label={t("wall_close", "Close")} className="text-[var(--foreground)]" onClick={() => guardedCloseRef.current()}>
            <X size={24} aria-hidden />
          </button>
          <h2 className="m-0 flex-1 text-lg font-bold leading-tight text-[var(--foreground)]">{heading}</h2>
          <button type="button" data-testid="offer-composer-submit" aria-busy={submitting} onClick={() => void submit()}
            className="h-12 rounded-full bg-[var(--primary)] px-[26px] text-sm font-bold text-[var(--primary-foreground)] aria-busy:opacity-60">
            {existing ? t("wall_save_button", "Save") : t("wall_post_button", "Post")}
          </button>
        </header>

        <div className="flex flex-col gap-3 px-4 pb-5 pt-4">
          <p className={`m-0 ${SECTION}`}>{t("apopilo_composer_kind", "Kind")}</p>
          <div data-testid="offer-composer-kinds" className="flex flex-wrap gap-2">
            {kinds.map(({ id, label }) => {
              const selected = draft.kind === id;
              return (
                <button key={id} type="button" data-testid={`offer-composer-kind-${id.toLowerCase()}`} aria-pressed={selected}
                  disabled={existing != null} // the kind never changes after the offer is posted (APOPILO_KIND_FIXED)
                  onClick={() => patch({ kind: id }, "dates")}
                  className={`rounded-full px-[14px] py-2 text-sm font-medium disabled:cursor-default ${
                    selected ? "bg-[var(--highlight)] text-[var(--highlight-foreground)]" : "border border-[var(--border-strong)] text-[var(--foreground)] disabled:opacity-60"
                  }`}>
                  {label}
                </button>
              );
            })}
          </div>

          <div data-testid="offer-composer-photos" className="flex flex-wrap gap-2">
            {draft.photos.map((p, i) => (
              <div key={p.fileId} data-testid={`offer-composer-photo-${i}`} className="relative h-[68px] w-[68px] overflow-hidden rounded-xl bg-[var(--bg-elevated)]">
                <img src={p.url} alt="" className="h-full w-full object-cover" />
                <button type="button" data-testid={`offer-composer-photo-remove-${i}`} aria-label={t("apopilo_composer_photo_remove", "Remove photo")}
                  className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--bg-surface)] text-[var(--foreground)]"
                  onClick={() => setDraft((d) => ({ ...d, photos: d.photos.filter((_, j) => j !== i) }))}>
                  <X size={12} aria-hidden />
                </button>
              </div>
            ))}
            {draft.photos.length < MAX_PHOTOS && (
              <button type="button" data-testid="offer-composer-photos-add" disabled={uploading > 0} onClick={() => photoInput.current?.click()}
                className="h-[68px] w-[68px] rounded-xl border border-[var(--primary-text)] text-sm font-bold text-[var(--primary-text)] disabled:opacity-60">
                {uploading > 0 ? "…" : t("apopilo_composer_photos_add", "+ Add")}
              </button>
            )}
            <input data-testid="offer-composer-photos-input" ref={photoInput} type="file" accept={IMAGE_ACCEPT} multiple hidden
              onChange={(e) => { void pickPhotos(e.target.files); e.target.value = ""; }} />
          </div>
          {photoFailed && <Err id="photos">{photoFailed === "svg" ? t("image_svg_too_large", "This SVG is over 1 MB") : t("apopilo_composer_photo_failed", "A photo didn't upload — try again")}</Err>}

          <div className="flex flex-col gap-1">
            <label className={ROW}>
              <Type size={22} className={ICON} aria-hidden />
              <input data-testid="offer-composer-title" data-autofocus className={INPUT} maxLength={120} aria-label={t("apopilo_composer_title", "Title")}
                placeholder={`${t("apopilo_composer_title", "Title")} *`} value={draft.title} onChange={(e) => patch({ title: e.target.value }, "title")} />
            </label>
            {shown.title && <Err id="title">{t("apopilo_composer_title_required", "Add a title")}</Err>}
          </div>
          <label className={ROW}>
            <AlignLeft size={22} className={ICON} aria-hidden />
            <textarea data-testid="offer-composer-description" className={`${INPUT} min-h-[48px] resize-y`} maxLength={4096} rows={2}
              aria-label={t("apopilo_composer_description", "Notes")} placeholder={t("apopilo_composer_description_ph", "What do you offer?")}
              value={draft.description} onChange={(e) => patch({ description: e.target.value })} />
          </label>

          <div className="flex flex-col gap-1">
            <div className={ROW}>
              <CalendarDays size={22} className={ICON} aria-hidden />
              <input data-testid="offer-composer-startsOn" type="date" className={INPUT} aria-label={t("apopilo_composer_starts", "First day")}
                min={existing ? undefined : tomorrowKey()} disabled={datesLocked} value={draft.startsOn}
                onChange={(e) => patch({ startsOn: e.target.value, ...(draft.endsOn === "" || draft.endsOn < e.target.value ? { endsOn: e.target.value } : {}) }, "dates")} />
              <span aria-hidden className="shrink-0 text-[var(--foreground-muted)]">→</span>
              <input data-testid="offer-composer-endsOn" type="date" className={INPUT} aria-label={t("apopilo_composer_ends", "Last day")}
                min={draft.startsOn || undefined} disabled={datesLocked} value={draft.endsOn} onChange={(e) => patch({ endsOn: e.target.value }, "dates")} />
            </div>
            {live && <p data-testid="offer-composer-live" className="m-0 text-xs font-medium text-[var(--foreground-muted)]">{live}</p>}
            {datesLocked && <p data-testid="offer-composer-dates-locked" className="m-0 text-xs text-[var(--foreground-subtle)]">
              {info!.status === "TAKEN" ? t("apopilo_composer_dates_taken", "The dates cannot change while the offer is taken.") : t("apopilo_composer_dates_locked", "The dates cannot change while requests are waiting.")}
            </p>}
            {shown.dates && <Err id="dates">{t("apopilo_composer_dates_invalid", "Pick dates after today, at most 90 days or nights")}</Err>}
          </div>

          <div className="flex flex-col gap-1">
            <div className={ROW}>
              <Building2 size={22} className={ICON} aria-hidden />
              {/* A place name is stored with its map point. */}
              <input data-testid="offer-composer-place" className={INPUT} maxLength={255} aria-label={t("apopilo_composer_place", "Town or place")}
                placeholder={draft.place ? t("apopilo_composer_place", "Town or place") : t("apopilo_composer_place_none", "Pick a point on the map")}
                disabled={draft.place == null} value={draft.place?.placeName ?? ""}
                onChange={(e) => setDraft((d) => (d.place ? { ...d, place: { ...d.place, placeName: e.target.value } } : d))} />
              {draft.place && <span data-testid="offer-composer-place-coords" className="shrink-0 text-xs text-[var(--foreground-muted)]">{formatCoords(draft.place)}</span>}
              <button type="button" data-testid="offer-composer-place-map" className={`${LINK} flex items-center gap-1`} onClick={() => setPickerOpen(true)}>
                <MapIcon size={18} aria-hidden />
                {t("event_place_map", "Map")}
              </button>
            </div>
          </div>
          {pickerOpen && (
            <LocationPickerModal
              initial={draft.place ? { lat: draft.place.lat, lng: draft.place.lng } : null}
              fallbackCenter={useMapStore.getState().lastCenter ?? DEFAULT_MAP_CENTER}
              onConfirm={(p) => { setDraft((d) => ({ ...d, place: { lat: p.lat, lng: p.lng, placeName: d.place?.placeName ?? null } })); setPickerOpen(false); }}
              onRemove={() => { setDraft((d) => ({ ...d, place: null })); setPickerOpen(false); }}
              onCancel={() => setPickerOpen(false)}
            />
          )}

          <p className="m-0 text-xs text-[var(--foreground-muted)]">
            {canCancelStay
              ? t("apopilo_composer_taken_hint", "This offer is taken ({{range}}). Cancelling tells the guest and puts your offer on hold.", { range })
              : t("apopilo_composer_hint", "Tutor: you host and teach, 20€ a day through IEUP. Apopilo: you offer a room and meals back, paid in Apopilo days. The caption shows on yellow.")}
          </p>
          {submitText && <p data-testid="offer-composer-submit-error" role="alert" className="m-0 text-sm text-[var(--destructive-text)]">{submitText}</p>}
          {canCancelStay && (
            <button type="button" data-testid="offer-composer-cancel-stay" className="h-11 self-center text-sm font-bold text-[var(--destructive-text)]" onClick={() => setConfirmCancel(true)}>
              {t("apopilo_composer_cancel_service", "Cancel service")}
            </button>
          )}
        </div>
      </div>
      {discardDialog}
      {cancelDialog}
    </div>
  );
}
